import { catchError, map, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { ApiService } from './api';
import { MediaFile } from '../gallery/media-file.model';
import { MediaType } from '../gallery/media-type.enum';
import { Observable, of, Subject} from 'rxjs';
import * as slug from 'slug';;
import { Store } from '@ngrx/store';
import * as MessagesActions from '@hub-store/messages/messages.actions';
import { FileItem } from '@atpco/hub-ui/lib/file-upload/file-upload.component';
import { FileDimensions } from '../gallery/file-dimensions.model';


@Injectable({ providedIn: 'root' })
export class MediaFileService {

  path: string = '/api';
  readonly MAX_FILE_SIZE_BYTES = 19922944;

  constructor(
    private apiService: ApiService,
    private store: Store,
  ) { }

  getMediaFiles(partnerId: number, mediaType: MediaType): Observable<MediaFile[]> {
    return this.apiService.get<{ data: MediaFile[] }>(`${this.path}/v2/airlines/${partnerId}/${mediaType}s`).pipe(map(media => media.data),catchError(() => of([])));
  }

  updateMediaFile(partnerId: number, mediaFile: Partial<MediaFile>, mediaType: MediaType) {
    return this.apiService.put(`${this.path}/v2/airlines/${partnerId}/${mediaType}s/${mediaFile.id}`, {
      name: this.sanitizeFileName(mediaFile.name),
      upa_category_ids: mediaFile.upa_category_ids,
      subfleet_ids: mediaFile.subfleet_ids,
      generic_cabin_ids: mediaFile.generic_cabin_ids,
      airport_code: mediaFile.airport_code,
      alt: mediaFile.alt,
      expiration_date: mediaFile.expiration_date,
      notes: mediaFile.notes
    });
  }

  updateMediaType(partnerId: number, photoId: number, mediaType: MediaType) {
    return this.apiService.put(`${this.path}/v2/airlines/${partnerId}/photos/${photoId}`, {
      photo_id: photoId,
      media_type: mediaType,
    });
  }

  deleteMediaFile(photoId: number, partnerId: number) {
    return this.apiService.delete(`${this.path}/v2/airlines/${partnerId}/photos/${photoId}`);
  }

  // Stages the file to S3 via a presigned URL and sends only the key; falls back to legacy multipart upload if staging is unavailable.
  uploadMediaFile(partnerId: number, file: any, dimentions: FileDimensions, mediaType: MediaType) {
    return this.stageFile(partnerId, file).pipe(
      switchMap(stagedKey => {
        if (stagedKey) {
          return this.apiService.post(`${this.path}/v2/airlines/${partnerId}/photos`, {
            photo: {
              staged_key: stagedKey,
              filename: this.sanitizeFileName(file.name),
              content_type: file.type,
              name: this.sanitizeFileName(file.name),
              width: dimentions.width.toString(),
              height: dimentions.height.toString(),
              media_type: mediaType,
            }
          });
        }
        const formData = new FormData();
        formData.append('photo[file]', file);
        formData.append('photo[name]', this.sanitizeFileName(file.name));
        formData.append('photo[width]', dimentions.width.toString());
        formData.append('photo[height]', dimentions.height.toString());
        formData.append('photo[media_type]', mediaType);
        return this.apiService.postForm(`${this.path}/v2/airlines/${partnerId}/photos`, formData);
      })
    );
  }

  uploadMediaFileReplace(partnerId: number, fileId: number, file: any, dimentions: FileDimensions, mediaType: MediaType) {
    return this.stageFile(partnerId, file).pipe(
      switchMap(stagedKey => {
        if (stagedKey) {
          return this.apiService.put(`${this.path}/v2/airlines/${partnerId}/photos/${fileId}`, {
            staged_key: stagedKey,
            filename: this.sanitizeFileName(file.name),
            content_type: file.type,
            width: dimentions.width.toString(),
            height: dimentions.height.toString(),
            media_type: mediaType,
          });
        }
        const formData = new FormData();
        formData.append('file', file, this.sanitizeFileName(file.name));
        formData.append('width', dimentions.width.toString());
        formData.append('height', dimentions.height.toString());
        formData.append('media_type', mediaType);
        return this.apiService.putForm(`${this.path}/v2/airlines/${partnerId}/photos/${fileId}`, formData);
      })
    );
  }

  // Presigns a staging key, PUTs the file to S3, and resolves to the key — or null on any failure, so callers can fall back to multipart.
  private stageFile(partnerId: number, file: any): Observable<string | null> {
    return this.apiService
      .post<{ upload_url: string; key: string }>(`${this.path}/v2/airlines/${partnerId}/photos/presign_upload`, {
        content_type: file.type,
      })
      .pipe(
        switchMap(({ upload_url, key }) => {
          if (!upload_url || !key) {
            return of(null);
          }
          return this.apiService
            .putRaw(upload_url, file, new HttpHeaders({ 'Content-Type': file.type }))
            .pipe(map(() => key));
        }),
        catchError(() => of(null))
      );
  }

  getMediaFileUploads(partnerId: number, mediaType: MediaType) {
    return this.apiService.get(`${this.path}/airlines/${partnerId}/photo_uploads?media_type=${mediaType}`);
  }

  getMediaFileUploadById(partnerId: number, id: number) {
    return this.apiService.get(`${this.path}/v2/airlines/${partnerId}/photos/${id}`);
  }

  getMediaFileById(id: number, mediaType: MediaType): Observable<MediaFile> {
    return this.apiService.get(`${this.path}/photos/${id}`);
  }

  deleteMediaFileUpload(id: number, mediaType: MediaType) {
    return this.apiService.delete(`${this.path}/photo_uploads/${id}?media_type=${mediaType}`);
  }

  /**
   * Transform file names into lower-case ASCII-chars only
   */
  sanitizeFileName(name: string | undefined): string {
    if (name) {
      const nameTokens = name.split('.');
      let ext = '';
      // use last element as file extension if it has one
      if (nameTokens.length > 1) {
        ext = '.' + nameTokens.pop();
      }
      return slug(nameTokens.join('').replace(/\_/g, '-')) + ext;
    } else {
      return '';
    }
  }

  uploadPhotoMediaFiles(mediaFiles: FileItem[], partnerId: number, mediaType: MediaType, photos?: any[]): Observable<{uploadedMedia: any[], mediaType: MediaType}> {
    const uploadComplete$ = new Subject<{uploadedMedia: any[], mediaType: MediaType}>();
    const uploadedMedia: any[] = [];
    let processedCount = 0;

    const onFileProcessed = () => {
      processedCount++;
      if (processedCount === mediaFiles.length) {
        if (uploadedMedia.length > 0) {
          this.store.dispatch(MessagesActions.setAppSnackbar({ message: 'Upload Successful' }));
        }
        uploadComplete$.next({ uploadedMedia, mediaType });
        uploadComplete$.complete();
      }
    };

    mediaFiles.forEach(fileItem => {
      const isFileSizeValid = fileItem.file.size <= this.MAX_FILE_SIZE_BYTES;
      this.validateDimensions(fileItem.file, (dimensions: FileDimensions) => {
        if (dimensions && isFileSizeValid) {
          this.uploadMediaFile(partnerId, fileItem.file, dimensions, mediaType).subscribe({
            next: (response) => {
              !!photos && (photos[photos.findIndex(photo => photo.isLoading)] = response);
              uploadedMedia.push(response);
              onFileProcessed();
            },
            error: (err) => {
              !!photos && photos.splice(0, 1);
              this.store.dispatch(MessagesActions.createExtendedErrorMessage({
                error: err,
                extraMessage: `"${fileItem.file.name}" failed to upload.`
              }));
              onFileProcessed();
            }
          });
        } else {
          !!photos && photos.splice(0, 1);
          const errorText = !isFileSizeValid ? `"${fileItem.file.name}" exceeds the maximum size of 19 MB.` : `"${fileItem.file.name}" is not valid.`;
          this.store.dispatch(MessagesActions.createErrorMessage({ errorText }));
          onFileProcessed();
        }
      });
    });
    return uploadComplete$;
  }

  validateDimensions(file: any, callback: any): any {
    const img = new Image();
    img.src = window.URL.createObjectURL(file);
    img.onload = function () {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      window.URL.revokeObjectURL(img.src);
      if (width > 10 && height > 10) {
        callback({ width, height });
      } else {
        callback(false);
      }
    };
    img.onerror = function () {
      window.URL.revokeObjectURL(img.src);
      callback(true);
    };
  }

  replaceFile(file: any, partnerId: number, mediaType: MediaType, photos: any[], selectedPhoto: any) {
    this.validateDimensions(file, (dimensions: FileDimensions) => {
      if (dimensions) {
        let replacedPhoto = selectedPhoto;
        this.uploadMediaFileReplace(partnerId, selectedPhoto.id, file, dimensions, mediaType).subscribe({
          next: (response) => {
            photos[photos.findIndex(photo => photo.id === selectedPhoto.id)] = response;
          }, 
          error: (err) => {
            photos.unshift(replacedPhoto);
            this.store.dispatch(MessagesActions.createExtendedErrorMessage({
              error: err,
              extraMessage: 'File replace failed.'
            }));
          }
        })
      } else {
        this.store.dispatch(MessagesActions.createErrorMessage({
          errorText: 'File is not valid.'
        }));
      }
    }) 
  }
}
